import { useState } from "react";

import { Link, useNavigate, useParams } from "react-router-dom";

import {
  ArrowLeft,
  Building2,
  Edit,
  ExternalLink,
  LayoutGrid,
  MapPin,
  Plus,
  Trash2,
} from "lucide-react";

import { PageContainer } from "~/components/layout/PageContainer";
import { PageHeader } from "~/components/layout/PageHeader";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "~/components/ui/alert-dialog";
import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "~/components/ui/card";
import { Skeleton } from "~/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import {
  useCreateHallway,
  useDeleteHallway,
  useHallways,
  usePositions,
  useRacks,
  useUpdateHallway,
  useWarehouse,
} from "~/hooks/wms";
import type { Hallway, HallwayFormData } from "~/types/wms";

import { HallwayDialog } from "./components/HallwayDialog";

export function WarehouseDetailPage() {
  const navigate = useNavigate();
  const { warehouseId } = useParams<{ warehouseId: string }>();

  // Dialog state
  const [hallwayDialogOpen, setHallwayDialogOpen] = useState(false);
  const [editingHallway, setEditingHallway] = useState<Hallway | undefined>();
  const [deleteHallway, setDeleteHallwayTarget] = useState<
    Hallway | undefined
  >();

  // Data queries
  const { data: warehouse, isLoading: warehouseLoading } =
    useWarehouse(warehouseId);
  const { data: hallways = [], isLoading: hallwaysLoading } =
    useHallways(warehouseId);

  // Get counts for stats - we need to fetch all racks and positions for this warehouse
  const { data: allRacks = [] } = useRacks();
  const { data: allPositions = [] } = usePositions();

  // Filter racks and positions that belong to this warehouse's hallways
  const hallwayIds = hallways.map((h) => h.id);
  const warehouseRacks = allRacks.filter((r) =>
    hallwayIds.includes(r.hallway_id)
  );
  const rackIds = warehouseRacks.map((r) => r.id);
  const warehousePositions = allPositions.filter((p) =>
    rackIds.includes(p.rack_id)
  );

  // Mutations
  const createHallway = useCreateHallway();
  const updateHallway = useUpdateHallway();
  const deleteHallwayMutation = useDeleteHallway();

  // Handlers
  const handleCreateHallway = () => {
    setEditingHallway(undefined);
    setHallwayDialogOpen(true);
  };

  const handleEditHallway = (hallway: Hallway) => {
    setEditingHallway(hallway);
    setHallwayDialogOpen(true);
  };

  const handleSubmitHallway = (data: HallwayFormData) => {
    if (editingHallway) {
      updateHallway.mutate(
        { id: editingHallway.id, data },
        { onSuccess: () => setHallwayDialogOpen(false) }
      );
    } else {
      createHallway.mutate(data, {
        onSuccess: () => setHallwayDialogOpen(false),
      });
    }
  };

  const confirmDelete = () => {
    if (!deleteHallway) return;
    deleteHallwayMutation.mutate(deleteHallway.id, {
      onSuccess: () => setDeleteHallwayTarget(undefined),
    });
  };

  if (warehouseLoading) {
    return (
      <PageContainer>
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-96 w-full" />
      </PageContainer>
    );
  }

  if (!warehouse) {
    return (
      <PageContainer>
        <div className="text-center text-muted-foreground">
          Bodega no encontrada
        </div>
        <Button variant="outline" onClick={() => navigate("/wms/locations")}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Volver a Bodegas
        </Button>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageHeader
        title={warehouse.name}
        description={`${warehouse.code}${warehouse.address ? ` • ${warehouse.address}` : ""}`}
        back={
          <Button
            variant="ghost"
            size="sm"
            className="-ml-2 h-9 text-muted-foreground"
            onClick={() => navigate("/wms/locations")}
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Volver
          </Button>
        }
        actions={
          <Link to={`/wms/ubicaciones?warehouse=${warehouseId}`}>
            <Button variant="outline">
              <ExternalLink className="mr-2 h-4 w-4" />
              Ver todas las ubicaciones
            </Button>
          </Link>
        }
      />

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Pasillos</p>
                <p className="text-3xl font-bold">{hallways.length}</p>
              </div>
              <Building2 className="h-8 w-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Racks</p>
                <p className="text-3xl font-bold">{warehouseRacks.length}</p>
              </div>
              <LayoutGrid className="h-8 w-8 text-green-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Posiciones</p>
                <p className="text-3xl font-bold">
                  {warehousePositions.length}
                </p>
              </div>
              <MapPin className="h-8 w-8 text-purple-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Hallways Table */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm font-medium">
              Pasillos ({hallways.length})
            </CardTitle>
            <Button size="sm" onClick={handleCreateHallway}>
              <Plus className="mr-1 h-4 w-4" />
              Nuevo Pasillo
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {hallwaysLoading ? (
            <div className="space-y-2">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : hallways.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <Building2 className="h-12 w-12 text-muted-foreground/50" />
              <h3 className="mt-4 text-lg font-medium">No hay pasillos</h3>
              <p className="mt-2 text-sm text-muted-foreground">
                Crea un pasillo para comenzar a agregar racks y posiciones
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Código</TableHead>
                  <TableHead>Código Completo</TableHead>
                  <TableHead>Nombre</TableHead>
                  <TableHead>Racks</TableHead>
                  <TableHead>Estado</TableHead>
                  <TableHead className="w-[100px]">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {hallways.map((hallway, index) => {
                  const hallwayRacks = warehouseRacks.filter(
                    (r) => r.hallway_id === hallway.id
                  );
                  return (
                    <TableRow
                      key={hallway.id}
                      style={{
                        animationDelay: `${Math.min(index * 40, 400)}ms`,
                      }}
                      className="duration-300 animate-in fade-in-0 slide-in-from-top-1 fill-mode-both"
                    >
                      <TableCell className="font-medium">
                        {hallway.code}
                      </TableCell>
                      <TableCell className="font-mono text-sm">
                        {hallway.full_code}
                      </TableCell>
                      <TableCell>{hallway.name || "-"}</TableCell>
                      <TableCell>{hallwayRacks.length}</TableCell>
                      <TableCell>
                        <Badge
                          variant={hallway.is_active ? "default" : "secondary"}
                        >
                          {hallway.is_active ? "Activo" : "Inactivo"}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleEditHallway(hallway)}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => setDeleteHallwayTarget(hallway)}
                          >
                            <Trash2 className="h-4 w-4 text-red-500" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Dialogs */}
      <HallwayDialog
        open={hallwayDialogOpen}
        onOpenChange={setHallwayDialogOpen}
        hallway={editingHallway}
        warehouseId={warehouseId}
        onSubmit={handleSubmitHallway}
        isLoading={createHallway.isPending || updateHallway.isPending}
      />

      {/* Delete confirmation */}
      <AlertDialog
        open={!!deleteHallway}
        onOpenChange={(open) => !open && setDeleteHallwayTarget(undefined)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar Pasillo</AlertDialogTitle>
            <AlertDialogDescription>
              ¿Está seguro de que desea eliminar el pasillo "
              {deleteHallway?.full_code}"? Esta acción eliminará también todos
              los racks y posiciones contenidos.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </PageContainer>
  );
}
